/**
 * Pending Approval Page
 * Shown when user's account is disabled and pending admin approval
 */

import { Clock } from "lucide-react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/useAuth";

// Re-check approval status this often while the user sits on this page.
// 30s balances "user gets through within a minute of admin approval"
// against backend load (one extra /api/auth/me per session per 30s).
const APPROVAL_POLL_INTERVAL_MS = 30_000;

export default function PendingApproval() {
  const { t } = useTranslation(["auth", "common"]);
  const { user, accountDisabled, signOut, refreshProfile } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!accountDisabled && user) {
      void navigate("/", { replace: true });
    }
  }, [accountDisabled, user, navigate]);

  // Poll for approval while the user is sitting on this page. The
  // existing visibility-based refresh in useProfileRefresh only fires
  // when the user switches tabs; a passive interval ensures they get
  // through within ~30s of admin approval without manual reload.
  useEffect(() => {
    if (!user || !accountDisabled) return;
    const interval = window.setInterval(() => {
      void refreshProfile();
    }, APPROVAL_POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [user, accountDisabled, refreshProfile]);

  const handleSignOut = async () => {
    await signOut();
    void navigate("/login");
  };

  return (
    <div className="min-h-[100dvh] bg-[var(--background)] px-4 py-12 sm:px-6 lg:px-8">

      <div className="mx-auto flex min-h-[calc(100dvh-6rem)] w-full max-w-5xl items-center justify-center">
        <div className="w-full max-w-xl">
          <div className="mb-6 text-center">
            <span className="inline-flex rounded-full border border-[var(--border)] bg-[var(--accent)] px-4 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--accent-foreground)]">
              {t("auth:accountPending.badge")}
            </span>
          </div>

          <div className="rounded-[var(--r-lg)] border border-[var(--border)] bg-[var(--card)] p-8 sm:p-10">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-[color:color-mix(in_srgb,var(--primary)_16%,transparent)] bg-[color:color-mix(in_srgb,var(--primary)_12%,var(--card))] text-[var(--primary)]">
              <Clock aria-hidden="true" className="h-8 w-8" />
            </div>

            <div className="text-center">
              <h1 className="text-3xl font-semibold tracking-tight text-[var(--foreground)]">
                {t("auth:accountPending.title")}
              </h1>
              <p className="mt-3 text-base leading-7 text-[var(--muted-foreground)]">
                {t("auth:accountPending.description")}
              </p>
            </div>

            {user?.email && (
              <div className="mt-6 rounded-[var(--r-md)] border border-[var(--border)] bg-[color:color-mix(in_srgb,var(--accent)_60%,var(--card))] px-4 py-3 text-center text-sm text-[var(--foreground)]">
                {t("auth:accountPending.registeredAs", { email: user.email })}
              </div>
            )}

            <div className="mt-8 flex justify-center">
              <Button
                variant="cta"
                size="lg"
                onClick={handleSignOut}
                data-test="account-pending-signout-btn"
              >
                {t("auth:accountPending.signOut")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
